// The decode: a wave turns old words into new ones, the same way the page's waves change
// everything else. The heading does it in a page transition, and a bio phrase does it when
// it's rewritten.
//
//   old letters   crest   trail, heavy to light   new letters, swelling then settled
//   J R  S m      ⊚       ◎ ◯ ◌ • ∘ ·             A b o u t ...
//                 the front moves this way: left to right -->
//
// `decodeFrame()` is the pure part: what every place shows for one position of the front.
// `decodeText()` measures the letters and draws the frames on an element.

export const CREST = "⊚";
export const TRAIL: readonly string[] = ["◎", "◯", "◌", "•", "∘", "·"];
/** The crest and the trail, in places. */
export const BAND = 1 + TRAIL.length;
/** How many places a new letter's weight swell lasts. */
const SWELL = 2;
/** How far a place starts early or late, in places. */
export const RAGGED = 0.4;
/** How often a trail glyph shows one tier off from where it should be. */
const SHIMMER = 0.12;
/** How much heavier a new letter swells than the text's own weight. */
export const SWELL_WEIGHT = 200;
/** How long a heading's decode takes. */
export const DECODE_MS = 950;
/** A no-break space (U+00A0), which stands in for a space while the text decodes. */
export const NO_BREAK_SPACE = " ";
// A place is a grapheme, so a letter with its accents counts as one place.
const graphemes = new Intl.Segmenter(undefined, { granularity: "grapheme" });

/** A text's places. A box holding nothing but a space doesn't get drawn (it's white space
    in a flex box), so spaces become no-break spaces while the text decodes. */
export function decodePlaces(text: string): string[] {
  return Array.from(graphemes.segment(text), ({ segment }) =>
    segment === " " ? NO_BREAK_SPACE : segment,
  );
}

/** The front's position, in places, at `progress` (0 to 1) through a decode of `count`
    places. It starts quick and eases into the last letters. At 0 every place shows its old
    text; at 1 every place has settled. */
export function decodeFront(progress: number, count: number): number {
  const span = count + BAND + SWELL + 2 * RAGGED;
  return (1 - (1 - progress) ** 1.7) * span - RAGGED;
}

export interface DecodePlan {
  old: readonly string[];
  next: readonly string[];
  /** Each place's letter width, in CSS pixels. */
  oldWidths: readonly number[];
  nextWidths: readonly number[];
  /** The widest glyph, in CSS pixels. */
  glyphWidth: number;
  /** The text's own font weight, which a letter lands at and settles back to. */
  weight: number;
  /** Each place's start offset, in places, at most `RAGGED` either way. */
  ragged: readonly number[];
}

/** What a place shows: a letter (tier null), the crest (tier -1), or a trail glyph (tier 0
    to `TRAIL.length - 1`, heavy to light), at some weight. A null width means the letter's
    own width. */
export interface DecodePlace {
  text: string;
  tier: number | null;
  weight: number;
  width: number | null;
}

export function decodeFrame(plan: DecodePlan, front: number, random: () => number): DecodePlace[] {
  const count = Math.max(plan.old.length, plan.next.length);
  const places: DecodePlace[] = [];
  for (let i = 0; i < count; i++) {
    const d = front - i + (plan.ragged[i] ?? 0);
    const oldWidth = plan.oldWidths[i] ?? 0;
    if (d < 0) {
      places.push({ text: plan.old[i] ?? "", tier: null, weight: plan.weight, width: oldWidth });
      continue;
    }
    if (d < BAND) {
      let tier = -1;
      if (d >= 1) {
        tier = Math.floor(d - 1);
        if (random() < SHIMMER) tier += random() < 0.5 ? -1 : 1;
        tier = Math.min(TRAIL.length - 1, Math.max(0, tier));
      }
      // The box is at least a glyph wide, so glyphs don't overlap and the text opens up
      // around the crest. It switches to the new letter's width as the crest passes. A
      // place that's going away keeps the old width until the trail has gone by.
      const letter = d < 0.5 ? oldWidth : (plan.nextWidths[i] ?? oldWidth);
      places.push({
        text: tier === -1 ? CREST : (TRAIL[tier] ?? ""),
        tier,
        weight: plan.weight,
        width: Math.max(plan.glyphWidth, letter),
      });
      continue;
    }
    const letter = plan.next[i];
    if (letter === undefined) {
      places.push({ text: "", tier: null, weight: plan.weight, width: 0 });
      continue;
    }
    // A new letter lands at the text's weight, so its width doesn't jump, then swells and
    // settles back.
    const swell = Math.min(1, (d - BAND) / SWELL);
    places.push({
      text: letter,
      tier: null,
      weight: Math.round(plan.weight + SWELL_WEIGHT * Math.sin(Math.PI * swell)),
      width: null,
    });
  }
  return places;
}

// The decode running on each element. A new decode finishes the old one first (a phrase
// rewritten again mid-decode, or a page restored from the back/forward cache mid-decode).
const runs = new WeakMap<HTMLElement, () => void>();

/** The element's own text. Mid-decode, it's in the element's name, which is what assistive
    technology reads then. */
export function shownText(element: HTMLElement): string {
  return element.getAttribute("aria-label") ?? element.textContent ?? "";
}

/** Ends the element's decode, if one is running, with its new text in place. */
export function endDecode(element: HTMLElement): void {
  runs.get(element)?.();
}

// Each text's width in the element's font, or as glyphs in the field's font, in CSS pixels.
function measure(element: HTMLElement, texts: readonly string[], className: string): number[] {
  const probe = document.createElement("span");
  probe.className = className;
  probe.setAttribute("aria-hidden", "true");
  element.append(probe);
  const widths = texts.map((text) => {
    probe.textContent = text;
    return probe.getBoundingClientRect().width;
  });
  probe.remove();
  return widths;
}

export interface DecodeOptions {
  /** A heading on its own line: it keeps its new text's width, and the places can overflow
      it. Without this, the text is part of a sentence and its lines break between words. */
  oneLine?: boolean;
  /** How long each place takes, in milliseconds, so a long text takes longer. Without
      this, the decode takes `DECODE_MS`. */
  placeMs?: number;
}

/** Decodes `element` from `from` to `to`. Assistive technology reads the new text the whole
    time (`aria-label`), and the places are hidden from it. `data-decoding` marks the run, so
    the text effects don't measure the page on every frame (text-effects.ts). */
export function decodeText(
  element: HTMLElement,
  from: string,
  to: string,
  options: DecodeOptions = {},
): void {
  endDecode(element);
  element.textContent = to;
  if (!from || from === to) return;
  // A heading keeps its new words' width while it decodes, so the header around it is laid
  // out like it will be at rest, from the first frame.
  const width = element.getBoundingClientRect().width;
  const old = decodePlaces(from);
  const next = decodePlaces(to);
  const count = Math.max(old.length, next.length);
  const plan: DecodePlan = {
    old,
    next,
    oldWidths: measure(element, old, "decode-place"),
    nextWidths: measure(element, next, "decode-place"),
    glyphWidth: Math.max(...measure(element, [CREST, ...TRAIL], "decode-place decode-glyph")),
    weight: Number.parseFloat(getComputedStyle(element).fontWeight) || 400,
    ragged: Array.from({ length: count }, () => (Math.random() * 2 - 1) * RAGGED),
  };
  const boxes = Array.from({ length: count }, () => {
    const box = document.createElement("span");
    box.className = "decode-place";
    box.setAttribute("aria-hidden", "true");
    return box;
  });
  element.setAttribute("aria-label", to);
  // In a sentence, the places only break where a space shows: the element doesn't wrap
  // (_page-transition.scss), and a space is a plain inline space that does.
  element.dataset.decoding = options.oneLine ? "" : "words";
  if (options.oneLine) element.style.setProperty("width", `${width}px`);
  element.replaceChildren(...boxes);
  const draw = (front: number): void => {
    decodeFrame(plan, front, Math.random).forEach((place, i) => {
      const box = boxes[i];
      if (!box) return;
      const space = !options.oneLine && place.tier === null && place.text === NO_BREAK_SPACE;
      const text = space ? " " : place.text;
      if (box.textContent !== text) box.textContent = text;
      box.className = space ? "decode-space" : "decode-place";
      box.classList.toggle("decode-glyph", place.tier !== null);
      if (place.tier === null) delete box.dataset.tier;
      else box.dataset.tier = String(place.tier);
      box.style.setProperty("font-weight", String(place.weight));
      if (place.width === null || space) box.style.removeProperty("width");
      else box.style.setProperty("width", `${place.width}px`);
    });
  };
  let frame = 0;
  const finish = (): void => {
    cancelAnimationFrame(frame);
    runs.delete(element);
    element.textContent = to;
    element.removeAttribute("aria-label");
    element.style.removeProperty("width");
    delete element.dataset.decoding;
  };
  runs.set(element, finish);
  const duration = options.placeMs === undefined ? DECODE_MS : count * options.placeMs;
  draw(decodeFront(0, count));
  const start = performance.now();
  const step = (now: number): void => {
    const progress = Math.min(1, (now - start) / duration);
    draw(decodeFront(progress, count));
    if (progress < 1) frame = requestAnimationFrame(step);
    else finish();
  };
  frame = requestAnimationFrame(step);
}
